import { useCallback, useEffect, useRef, useState } from "react";
import {
  LiveAvatarSession,
  SessionEvent,
  SessionDisconnectReason,
  SessionState,
  AgentEventsEnum,
} from "@heygen/liveavatar-web-sdk";
import fetchHeygenAccessToken from "@/services/heygen/fetchHeygenAccessToken";
import toaster from "@/lib/toaster";
import { ChatStore } from "@/stores/ChatStore";
import normalizeMessage from "@/lib/normalizeMessage";
import useIdleTimeout from "./useIdleTimeout";
import { v4 } from "uuid";
import SettingsStore from "@/stores/SettingsStore";
import saveSessionId from "@/services/queries/saveSessionId";
import type { TAvatar } from "@/types/TAvatar";

/** HeyGen server-side idle is ~2-3 min; ping before that to keep the stream alive. */
const KEEP_ALIVE_INTERVAL_MS = 45_000;
/** Fallback when SDK swallows session.stopped as "unsupported event type". */
const SESSION_STATE_POLL_MS = 2_000;

export default function useAvatarSession() {
  const [isLoadingSession, setIsLoadingSession] = useState(false);
  const [isStreamReady, setIsStreamReady] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState<TAvatar | undefined>();
  const session = useRef<LiveAvatarSession | null>(null);
  /** Bumps on every start/stop so async start work can detect being superseded. */
  const sessionGeneration = useRef(0);
  /** True while we intentionally call stop() so SESSION_DISCONNECTED is not treated as idle. */
  const stoppingLocally = useRef(false);
  const keepAliveTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const statePollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const unexpectedDisconnectHandled = useRef(false);
  const resetDeltaBuffer = ChatStore(state => state.resetDeltaBuffer);
  const addMessage = ChatStore(state => state.addMessage);
  const setAvatarStatus = ChatStore(state => state.setAvatarStatus);
  const setIsRunning = ChatStore(state => state.setIsRunning);
  const setIsAvatarReady = ChatStore(state => state.setIsAvatarReady);
  const setIsAvatarSpeaking = ChatStore(state => state.setIsAvatarSpeaking);
  const avatars = SettingsStore(state => state.avatars);
  const welcomeMessage = SettingsStore(state => state.welcomeMessage);

  const clearSessionMonitors = useCallback(() => {
    if (keepAliveTimer.current) {
      clearInterval(keepAliveTimer.current);
      keepAliveTimer.current = null;
    }
    if (statePollTimer.current) {
      clearInterval(statePollTimer.current);
      statePollTimer.current = null;
    }
  }, []);

  const handleUnexpectedAvatarDisconnect = useCallback(
    (reason: string) => {
      if (stoppingLocally.current || unexpectedDisconnectHandled.current) return;
      if (!ChatStore.getState().isRunning || ChatStore.getState().sessionTimedOut) return;

      unexpectedDisconnectHandled.current = true;
      console.warn("Avatar session lost unexpectedly:", reason);
      clearSessionMonitors();
      session.current = null;
      setIsStreamReady(false);
      setIsAvatarReady(false);
      setIsAvatarSpeaking(false);
      ChatStore.getState().handleSessionTimeout();
    },
    [clearSessionMonitors, setIsAvatarReady, setIsAvatarSpeaking],
  );

  const startSessionMonitors = useCallback(
    (generation: number) => {
      clearSessionMonitors();
      unexpectedDisconnectHandled.current = false;

      keepAliveTimer.current = setInterval(() => {
        if (generation !== sessionGeneration.current || !session.current) return;
        session.current.keepAlive().catch(error => {
          console.warn("Avatar keepAlive failed:", error);
          handleUnexpectedAvatarDisconnect("keepalive_failed");
        });
      }, KEEP_ALIVE_INTERVAL_MS);

      statePollTimer.current = setInterval(() => {
        if (generation !== sessionGeneration.current || !session.current) return;
        const state = session.current.state;
        if (state === SessionState.DISCONNECTED || state === SessionState.DISCONNECTING) {
          handleUnexpectedAvatarDisconnect(`state_poll:${state}`);
        }
      }, SESSION_STATE_POLL_MS);
    },
    [clearSessionMonitors, handleUnexpectedAvatarDisconnect],
  );

  useEffect(() => clearSessionMonitors, [clearSessionMonitors]);

  const selectRandomAvatar = useCallback(() => {
    const weightedAvatars = avatars.filter(a => (a.weight ?? 1.0) > 0);
    if (weightedAvatars.length === 0) return avatars[0];

    const totalWeight = weightedAvatars.reduce((sum, a) => sum + (a.weight ?? 1.0), 0);
    let random = Math.random() * totalWeight;

    for (const avatar of weightedAvatars) {
      random -= (avatar.weight ?? 1.0);
      if (random <= 0) return avatar;
    }

    return weightedAvatars[0];
  }, [avatars]);

  const endSession = useCallback(async () => {
    sessionGeneration.current += 1;
    clearSessionMonitors();
    const current = session.current;
    session.current = null;
    setIsStreamReady(false);
    setIsAvatarReady(false);
    setIsAvatarSpeaking(false);

    if (!current) return;

    stoppingLocally.current = true;
    try {
      await current.stop();
    } catch (error) {
      // Common during restart races when LiveAvatar is already disconnected.
      console.warn("Avatar session stop skipped or failed:", error);
    } finally {
      stoppingLocally.current = false;
    }
  }, [clearSessionMonitors, setIsAvatarReady, setIsAvatarSpeaking]);

  const startSession = useCallback(async () => {
    const generation = ++sessionGeneration.current;
    clearSessionMonitors();
    unexpectedDisconnectHandled.current = false;
    setIsLoadingSession(true);

    const newAvatar = selectRandomAvatar();
    setSelectedAvatar(newAvatar);

    try {
      const tokenResponse = await fetchHeygenAccessToken();
      if (generation !== sessionGeneration.current) return;

      if (!tokenResponse.session_token) {
        console.error("Failed to get session token");
        setIsRunning(false);
        return;
      }

      if (!ChatStore.getState().isRunning) {
        return;
      }

      // Ensure any previous instance is cleared before assigning a new one.
      if (session.current) {
        stoppingLocally.current = true;
        try {
          await session.current.stop();
        } catch {
          // ignore
        } finally {
          stoppingLocally.current = false;
        }
        session.current = null;
      }

      if (generation !== sessionGeneration.current || !ChatStore.getState().isRunning) {
        return;
      }

      const liveSession = new LiveAvatarSession(tokenResponse.session_token, {
        voiceChat: false,
      });
      session.current = liveSession;

      liveSession.on(SessionEvent.SESSION_STREAM_READY, () => {
        if (generation !== sessionGeneration.current || !ChatStore.getState().isRunning) {
          console.warn("Avatar session ready, but start was cancelled. Stopping session.");
          void endSession();
          return;
        }

        saveSessionId(tokenResponse.session_id);
        setIsStreamReady(true);
        setAvatarStatus("Bereit");
        setIsAvatarReady(true);
        toaster("Avatar Stream ist bereit");
        startSessionMonitors(generation);

        // Skip welcome when continuing an existing chat after an idle timeout.
        const hasExistingChat = ChatStore.getState().messages.length > 0;
        if (welcomeMessage && !hasExistingChat) {
          addMessage({
            transcript: welcomeMessage,
            sender: "assistant",
            id: v4(),
            timestamp: new Date(),
          });
          setTimeout(() => {
            if (generation === sessionGeneration.current) {
              session.current?.repeat(normalizeMessage(welcomeMessage));
            }
          }, 500);
        }
      });

      liveSession.on(SessionEvent.SESSION_STATE_CHANGED, state => {
        if (generation !== sessionGeneration.current) return;
        if (state === SessionState.DISCONNECTED || state === SessionState.DISCONNECTING) {
          handleUnexpectedAvatarDisconnect(`state_changed:${state}`);
        }
      });

      // HeyGen can idle-timeout the LiveKit avatar (~2-3 min) while SchadenKI/chat
      // still works — without this we get a white screen and zombie "Bereit" UI.
      liveSession.on(SessionEvent.SESSION_DISCONNECTED, reason => {
        if (generation !== sessionGeneration.current) return;
        if (stoppingLocally.current) return;
        if (reason === SessionDisconnectReason.CLIENT_INITIATED) return;
        handleUnexpectedAvatarDisconnect(`session_disconnected:${reason}`);
      });

      liveSession.on(AgentEventsEnum.AVATAR_SPEAK_STARTED, () => {
        if (generation !== sessionGeneration.current) return;
        setIsAvatarSpeaking(true);
        setAvatarStatus("Sprechen");
      });

      liveSession.on(AgentEventsEnum.AVATAR_SPEAK_ENDED, () => {
        if (generation !== sessionGeneration.current) return;
        setIsAvatarSpeaking(false);
        setAvatarStatus("Bereit");
      });

      await liveSession.start();
    } catch (error) {
      if (generation === sessionGeneration.current) {
        console.error("Error starting avatar session:", error);
        setIsRunning(false);
      }
    } finally {
      if (generation === sessionGeneration.current) {
        setIsLoadingSession(false);
      }
    }
  }, [
    selectRandomAvatar,
    welcomeMessage,
    setAvatarStatus,
    setIsAvatarReady,
    setIsRunning,
    setIsAvatarSpeaking,
    addMessage,
    endSession,
    clearSessionMonitors,
    startSessionMonitors,
    handleUnexpectedAvatarDisconnect,
  ]);

  const handleIdleTimeout = useCallback(() => {
    void endSession();
    ChatStore.getState().handleSessionTimeout();
  }, [endSession]);

  const { resetIdleTimer } = useIdleTimeout({
    callback: handleIdleTimeout,
    enabled: isStreamReady,
  });

  const handleSpeak = useCallback(
    async (message?: string) => {
      if (!message) return;
      const normalizedMessage = normalizeMessage(message);
      resetIdleTimer();
      try {
        const current = session.current;
        if (!current || current.state === SessionState.DISCONNECTED || current.state === SessionState.DISCONNECTING) {
          handleUnexpectedAvatarDisconnect("speak_while_disconnected");
          return;
        }
        current.repeat(normalizedMessage);
      } catch (error) {
        console.error("Error getting response:", error);
        handleUnexpectedAvatarDisconnect("speak_failed");
      }
    },
    [resetIdleTimer, handleUnexpectedAvatarDisconnect],
  );

  const interruptAvatar = useCallback(() => {
    console.log("interrupt()");
    session.current?.interrupt();
    resetDeltaBuffer();
  }, [resetDeltaBuffer]);

  const attachVideo = useCallback((element: HTMLMediaElement | null) => {
    if (element && session.current) {
      session.current.attach(element);
    }
  }, []);

  return {
    isStreamReady,
    isLoadingSession,
    startSession,
    endSession,
    handleSpeak,
    interruptAvatar,
    selectedAvatar,
    attachVideo,
  };
}
